import { BoardLayout } from "@/components/board/BoardLayout";
import { ErrorBanner } from "@/components/board/ErrorBanner";
import { LoadingState } from "@/components/board/LoadingState";
import { MakersBoard } from "@/components/makers/MakersBoard";
import { OooProvider } from "@/contexts/OooContext";
import { useMakersBoardData } from "@/hooks/useMakersBoardData";
import { useQueryParams } from "@/hooks/useQueryParams";
import { useUserTimeZone } from "@/hooks/useUserTimeZone";
import { useOffices } from "@/contexts/OfficesContext";
import { consumeMakersEmployeeFilter } from "@/utils/makersDeepLink";
import { addDays, addWeeks, currentWeekStartInTimeZone, parseISODate } from "@/utils/dateUtils";
import { computeNumWeeks, getDefaultWeekCount } from "@/utils/weekUtils";
import type { ReactNode } from "react";
import { getEmployeeDisplayName } from "@/utils/nameUtils";
import { useEffect, useMemo, useRef, useState } from "react";

interface MakersTabProps {
  // Rendered as the first child of the board (the page-level tab switcher).
  header?: ReactNode;
}

/**
 * "Makers" tab - the makers board (capacity/assignments per employee).
 */
export function MakersTab({ header }: MakersTabProps) {
  const {
    employees,
    loading,
    error,
    updateCapacityOptimistically,
    updateEmployeeCapacity,
    updateEmployeeRoles,
    updateRolesOptimistically,
    updateNameOptimistically,
    updateEmployeeName,
    updateEmployeeNickname,
    updateNicknameOptimistically,
    refreshSilently,
  } = useMakersBoardData();
  const { selectedOffices } = useOffices();
  const userTimeZone = useUserTimeZone();
  const { get, setParam } = useQueryParams();

  // Deep-link hydration: prefer the sessionStorage handoff (in-app navigation),
  // falling back to a `?employee=id1,id2` URL param (external deep links).
  // On the fallback path, also strip the param from the URL via replaceState.
  const [initialSearchPills, setInitialSearchPills] = useState<string[] | undefined>(undefined);
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (hydratedRef.current) return;
    if (loading) return;
    hydratedRef.current = true;

    const byId = new Map(employees.map((e) => [e.id, getEmployeeDisplayName(e)] as const));

    const stashedIds = consumeMakersEmployeeFilter();
    if (stashedIds) {
      const pills = stashedIds
        .map((id) => byId.get(id))
        .filter((name): name is string => Boolean(name));
      setInitialSearchPills(pills);
      return;
    }

    const employeeParam = get("employee");
    if (!employeeParam) {
      setInitialSearchPills([]);
      return;
    }

    const ids = employeeParam.split(",").filter(Boolean);
    const pills = ids
      .map((id) => byId.get(id))
      .filter((name): name is string => Boolean(name));
    setInitialSearchPills(pills);

    setParam("employee", null, { replace: true });
  }, [loading, employees, get, setParam]);

  const startDate = useMemo(
    () => currentWeekStartInTimeZone(userTimeZone),
    [userTimeZone],
  );

  const numWeeks = useMemo(() => {
    const defaultEnd = addDays(startDate, (getDefaultWeekCount() - 1) * 7);
    let furthestEnd = defaultEnd;

    for (const employee of employees) {
      for (const assignment of employee.assignments) {
        const project = assignment.project;
        if (project?.start_date && project.num_weeks) {
          const projectEnd = addWeeks(parseISODate(project.start_date), project.num_weeks);
          if (projectEnd > furthestEnd) {
            furthestEnd = projectEnd;
          }
        }
      }
    }

    return computeNumWeeks(startDate, furthestEnd);
  }, [startDate, employees]);

  return (
    <BoardLayout fill>
      {header}
      {error ? <ErrorBanner message={error} entityName="employees" /> : null}

      {loading || initialSearchPills === undefined ? (
        <LoadingState message="Loading employees..." />
      ) : (
        <OooProvider>
          <MakersBoard
            employees={employees}
            loading={loading}
            error={error}
            startDate={startDate}
            numWeeks={numWeeks}
            selectedOffices={selectedOffices}
            updateCapacityOptimistically={updateCapacityOptimistically}
            updateEmployeeCapacity={updateEmployeeCapacity}
            updateEmployeeRoles={updateEmployeeRoles}
            updateRolesOptimistically={updateRolesOptimistically}
            updateNameOptimistically={updateNameOptimistically}
            updateEmployeeName={updateEmployeeName}
            updateEmployeeNickname={updateEmployeeNickname}
            updateNicknameOptimistically={updateNicknameOptimistically}
            initialSearchPills={initialSearchPills}
            onRefresh={refreshSilently}
            showDeleteForManual
          />
        </OooProvider>
      )}
    </BoardLayout>
  );
}
